import LinkIcon from '@plone/volto/icons/link.svg';
import HomeIcon from '@plone/volto/icons/home.svg';
import FolderIcon from '@plone/volto/icons/folder.svg';
import UserIcon from '@plone/volto/icons/user.svg';
import PencilIcon from '@plone/volto/icons/pencil.svg';
import BriefcaseIcon from '@plone/volto/icons/briefcase.svg';
import FingerPrintIcon from '@plone/volto/icons/finger-print.svg';
import BellIcon from '@plone/volto/icons/bell-ringing.svg';

// Base icons that are always available
const baseIcons = {
  link: LinkIcon,
  home: HomeIcon,
  folder: FolderIcon,
  user: UserIcon,
  pencil: PencilIcon,
  briefcase: BriefcaseIcon,
  fingerprint: FingerPrintIcon,
  bell: BellIcon,
};

// Dynamically import custom icons from the icons folder
let customIcons = {};
try {
  const customIconsContext = require.context('./icons', false, /\.svg$/);
  customIcons = customIconsContext.keys().reduce((icons, iconPath) => {
    // Extract filename without extension as the key
    // e.g., './custom-icon.svg' becomes 'custom-icon'
    const iconName = iconPath.replace(/^\.\//, '').replace(/\.svg$/, '');
    const iconModule = customIconsContext(iconPath);
    // Handle both default exports and direct exports
    icons[iconName] = iconModule.default || iconModule;
    return icons;
  }, {});
} catch (e) {
  // Icons folder doesn't exist yet, that's okay
  console.log('No custom icons folder found');
}

// Merge base icons with custom icons (custom icons can override base ones)
export const iconChoices = {
  ...baseIcons,
  ...customIcons,
};

const schema = () => ({
  title: 'Icon Link Row',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'preheader',
        'headerText',
        'header',
        'headerAlignment',
        'headerColor',
        'itemsAlignment',
        'columns',
        'backgroundColor',
        'itemsColor',
        'itemBackgroundColor',
        'items',
        'buttonLabel',
        'buttonLink',
        'showButton',
        'mobileCarousel',
        'customClass',
      ],
    },
  ],
  properties: {
    preheader: {
      title: 'Preheader',
      type: 'string',
      description: 'Optional text above the main header',
    },
    headerText: {
      title: 'Header Text',
      type: 'string',
      description: 'Main header for the block (used for TOC)',
    },
    header: {
      title: 'Subheader',
      type: 'string',
      description: 'Optional subheader text',
    },
    headerAlignment: {
      title: 'Header alignment',
      widget: 'align',
      actions: ['left', 'center', 'right'],
      default: 'center',
    },
    headerColor: {
      title: 'Header text color',
      choices: [
        ['light', 'Light (for dark backgrounds)'],
        ['dark', 'Dark (for light backgrounds)'],
      ],
      type: 'string',
      default: 'dark',
      description: 'Color of header and subheader text',
    },
    itemsAlignment: {
      title: 'Items alignment',
      widget: 'align',
      actions: ['left', 'center', 'right'],
      default: 'center',
    },
    columns: {
      title: 'Number of items per row (desktop)',
      type: 'number',
      minimum: 2,
      maximum: 8,
      default: 4,
      description: 'How many items to display per row on desktop',
    },
    backgroundColor: {
      title: 'Background color',
      choices: [
        ['none', 'None (transparent)'],
        ['#fff', 'White'],
        ['var(--off-white)', 'Off white'],
        ['var(--grey-light)', 'Light grey'],
        ['var(--grey-mid)', 'Mid grey'],
        ['var(--charcoal)', 'Chardcoal'],
      ],
      type: 'string',
      default: 'none',
      description: 'Background color for the entire block',
    },
    itemsColor: {
      title: 'Items text and icon color',
      choices: [
        ['light', 'Light (for dark backgrounds)'],
        ['dark', 'Dark (for light backgrounds)'],
      ],
      type: 'string',
      default: 'dark',
      description: 'Color of item text, icons, and mobile carousel controls',
    },
    itemBackgroundColor: {
      title: 'Item background color',
      choices: [
        ['none', 'None (transparent)'],
        ['#fff', 'White'],
        ['var(--blue-lighter)', 'Light blue'],
        ['var(--blue-base)', 'Blue'],
        ['var(--teal-lighter)', 'Light teal'],
        ['var(--teal-base)', 'Teal'],
        ['var(--gold-lighter)', 'Light gold'],
        ['var(--gold-base)', 'Gold'],
        ['var(--coral-lighter)', 'Light coral'],
        ['var(--coral-base)', 'Coral'],
      ],
      type: 'string',
      default: 'none',
      description: 'Background color for individual items',
    },
    items: {
      title: 'Links',
      widget: 'object_list',
      default: [
        {
          icon: 'link',
          heading: 'Link',
          text: '',
          href: '',
        },
      ],
      schema: {
        title: 'Link',
        fieldsets: [
          {
            id: 'default',
            title: 'Default',
            fields: ['icon', 'preheader', 'heading', 'text', 'href', 'textColor', 'backgroundColor'],
          },
        ],
        properties: {
          icon: {
            title: 'Icon',
            type: 'string',
            default: 'link',
            choices: Object.keys(iconChoices).map((key) => [key, key]),
          },
          preheader: {
            title: 'Preheader',
            type: 'string',
            description: 'Optional text above the heading',
          },
          heading: {
            title: 'Heading',
            type: 'string',
            default: 'Link',
          },
          text: {
            title: 'Description',
            widget: 'slate',
          },
          href: {
            title: 'Link (optional)',
            widget: 'object_browser',
            mode: 'link',
            allowExternals: true,
          },
          textColor: {
            title: 'Text and icon color (override)',
            choices: [
              ['', 'Use default'],
              ['light', 'Light (for dark backgrounds)'],
              ['dark', 'Dark (for light backgrounds)'],
            ],
            type: 'string',
            default: '',
            description: 'Override the default text and icon color for this item only',
          },
          backgroundColor: {
            title: 'Background color (override)',
            choices: [
              ['', 'Use default'],
              ['none', 'None (transparent)'],
              ['#fff', 'White'],
              ['var(--blue-lighter)', 'Light blue'],
              ['var(--blue-base)', 'Blue'],
              ['var(--teal-lighter)', 'Light teal'],
              ['var(--teal-base)', 'Teal'],
              ['var(--gold-lighter)', 'Light gold'],
              ['var(--gold-base)', 'Gold'],
              ['var(--coral-lighter)', 'Light coral'],
              ['var(--coral-base)', 'Coral'],
            ],
            type: 'string',
            default: '',
            description: 'Override the default item background color for this item only',
          },
        },
        required: [],
      },
    },
    buttonLabel: {
      title: 'Button text',
      type: 'string',
    },
    buttonLink: {
      title: 'Button link',
      widget: 'object_browser',
      mode: 'link',
      allowExternals: true,
    },
    showButton: {
      title: 'Always show button',
      type: 'boolean',
      default: false,
    },
    mobileCarousel: {
      title: 'Use carousel on mobile',
      type: 'boolean',
      default: false,
      description:
        'On small screens this row becomes a carousel with dots, one item per slide, and automatic scrolling.',
    },
    customClass: {
      title: 'Custom CSS class',
      type: 'string',
      description: 'Add a custom CSS class to the block wrapper',
    },
  },
  required: [],
});

export default schema;
